/**
 * Music & ambience state machine (08 §6). Tension is added by adding stems;
 * stem changes land on the next beat boundary except the COUNTDOWN cut, the
 * GO entry and the ROUND_END duck, which are immediate. All stems share one
 * start time and loop over exactly AUDIO_STEM_LEN_S so any subset stays
 * phase-locked.
 */
import { AUDIO_BEAT_S, AUDIO_STEM_LEN_S, AUDIO_STEM_XFADE_MS } from './constants.js'
import type { AudioEngine, VoiceHandle } from './engine.js'
import type { SoundId } from './manifest.js'

export type MusicState = 'HUB' | 'LOBBY_PICK' | 'LOBBY_LOCK' | 'COUNTDOWN' | 'LIVE' | 'LIVE_LAST10' | 'OVERTIME' | 'ROUND_END' | 'BETWEEN' | 'RESULTS' | 'OFF'

export interface MusicInfo { roundIndex?: number; now?: number }

const STEM_IDS: SoundId[] = ['stem_tick', 'stem_pulse_low', 'stem_kit', 'stem_metal', 'stem_low_hits', 'stem_ride', 'stem_drone', 'stem_hub', 'stem_results']
const AMB_IDS: SoundId[] = ['amb_hub_room', 'amb_kiosk_hum', 'amb_arena']

export function stemsFor(state: MusicState, roundIndex: number, opts: { hubMusic: boolean; reduced: boolean }): SoundId[] {
  let set: SoundId[]
  switch (state) {
    case 'HUB': set = opts.hubMusic ? ['stem_hub'] : []; break
    case 'LOBBY_PICK': set = ['stem_tick']; break
    case 'LOBBY_LOCK': set = ['stem_tick', 'stem_pulse_low']; break
    case 'COUNTDOWN': set = []; break
    case 'LIVE': case 'LIVE_LAST10': case 'OVERTIME': case 'ROUND_END': {
      set = ['stem_tick', 'stem_kit']
      if (roundIndex >= 1) set.push('stem_metal')
      if (roundIndex >= 2) set.push('stem_low_hits')
      if (state === 'LIVE_LAST10' || state === 'OVERTIME') set.push('stem_ride')
      if (state === 'OVERTIME') set.push('stem_drone')
      break
    }
    case 'BETWEEN': set = ['stem_tick']; break
    case 'RESULTS': set = ['stem_results']; break
    case 'OFF': set = []; break
  }
  if (opts.reduced) set = set.filter((s) => s === 'stem_tick' || s === 'stem_hub' || s === 'stem_results').slice(0, 1)
  return set
}

export function ambienceFor(state: MusicState, reduced: boolean): SoundId[] {
  if (reduced) return []
  switch (state) {
    case 'HUB': return ['amb_hub_room', 'amb_kiosk_hum']
    case 'COUNTDOWN': case 'LIVE': case 'LIVE_LAST10': case 'OVERTIME': case 'ROUND_END': case 'BETWEEN': return ['amb_arena']
    default: return []
  }
}

/** 08 §6 table crossfades: HUB ↔ LOBBY_PICK 400 ms, RESULTS in 800 ms. */
export const HUB_XFADE_MS = 400
export const RESULTS_XFADE_MS = 800

/** Immediate transitions (08 §6 rules). */
export const IMMEDIATE: MusicState[] = ['COUNTDOWN', 'LIVE', 'ROUND_END']

export class MusicStateMachine {
  state: MusicState = 'OFF'
  roundIndex = 0
  hubMusic = false
  reduced = false
  /** ctx time the current stem set started (08 §6 beat grid origin); boundaries are
   *  origin + k × beat. Anchored to the FIRST stem start (LOBBY_PICK), not GO, so
   *  pre-GO stem changes are on the beat too; the COUNTDOWN cut resets it and GO
   *  re-anchors (the bed enters on the GO beat). */
  beatOrigin = 0
  private active = new Map<SoundId, VoiceHandle>()
  private ambience = new Map<SoundId, VoiceHandle>()
  private pending: { at: number; stems: SoundId[] } | null = null
  private stemStart: number | null = null
  private kioskPos: { x: number; z: number } | null = null

  constructor(private readonly engine: AudioEngine) {}

  setKioskPos(p: { x: number; z: number }): void { this.kioskPos = p }

  /** Next beat boundary strictly after `now`. */
  nextBoundary(now: number): number {
    const k = Math.floor((now - this.beatOrigin) / AUDIO_BEAT_S) + 1
    return this.beatOrigin + k * AUDIO_BEAT_S
  }

  setState(state: MusicState, info: MusicInfo = {}): void {
    const now = info.now ?? this.engine.ctx.currentTime
    if (info.roundIndex !== undefined) this.roundIndex = info.roundIndex
    const prev = this.state
    this.state = state
    this.engine.musicState = state
    if (state === 'ROUND_END') this.engine.roundEndDuck(true)
    else if (prev === 'ROUND_END') this.engine.roundEndDuck(false)
    const target = stemsFor(state, this.roundIndex, { hubMusic: this.hubMusic, reduced: this.reduced })
    // 08 §6 table: HUB ↔ LOBBY_PICK crossfade 400 ms, RESULTS in 800 ms / out 400 ms; beat-quantised otherwise.
    const xfade = state === 'RESULTS' ? RESULTS_XFADE_MS : state === 'HUB' || prev === 'HUB' ? HUB_XFADE_MS : AUDIO_STEM_XFADE_MS
    const crossfade = state === 'HUB' || state === 'RESULTS' || prev === 'HUB'
    if (IMMEDIATE.includes(state) || prev === 'OFF' || state === 'OFF' || crossfade) { this.pending = null; this.applyStems(target, now, xfade, crossfade) }
    else this.pending = { at: this.nextBoundary(now), stems: target }
    this.applyAmbience(ambienceFor(state, this.reduced))
  }

  /** Call every frame: lands pending stem changes on their beat. */
  tick(now = this.engine.ctx.currentTime): void {
    if (this.pending && now >= this.pending.at - 0.005) { const p = this.pending; this.pending = null; this.applyStems(p.stems, p.at) }
  }

  private applyStems(target: SoundId[], at: number, xfadeMs = AUDIO_STEM_XFADE_MS, fadeIn = false): void {
    const e = this.engine
    for (const [id, h] of [...this.active]) if (!target.includes(id)) { h.stop(xfadeMs); this.active.delete(id) }
    if (!target.length) { this.stemStart = null; return }
    if (!e.unlocked) return
    // Phase lock: a stem joining later starts at the offset the running set has reached.
    // A fresh set (nothing running) re-anchors the beat grid; crossfade transitions (HUB,
    // RESULTS) ramp the incoming stems in, while the GO entry and beat-quantised adds
    // enter at full level on their beat.
    const fresh = this.stemStart === null || !this.active.size
    if (fresh) { this.stemStart = at; this.beatOrigin = at }
    const offset = ((at - this.stemStart!) % AUDIO_STEM_LEN_S + AUDIO_STEM_LEN_S) % AUDIO_STEM_LEN_S
    for (const id of target) {
      if (this.active.has(id)) continue
      // One start() per source: the phase offset rides the play() call (a second start() throws
      // InvalidStateError and, inside frame(), would have killed the render loop).
      const phase = offset > 0 && id !== 'stem_drone' && id !== 'stem_results' ? offset : undefined
      const h = e.play(id, { startAt: at, offset: phase, entityKey: `stem:${id}`, loop: id !== 'stem_drone', fadeInMs: fadeIn ? xfadeMs : undefined })
      if (h) this.active.set(id, h)
    }
  }

  private applyAmbience(target: SoundId[]): void {
    const e = this.engine
    for (const [id, h] of [...this.ambience]) if (!target.includes(id)) { h.stop(400); this.ambience.delete(id) }
    if (!e.unlocked) return
    for (const id of target) {
      if (this.ambience.has(id)) continue
      const h = e.play(id, { entityKey: `amb:${id}`, pos: id === 'amb_kiosk_hum' ? this.kioskPos ?? undefined : undefined })
      if (h) this.ambience.set(id, h)
    }
  }

  setOptions(o: { hubMusic: boolean; reduced: boolean }): void {
    const changed = o.hubMusic !== this.hubMusic || o.reduced !== this.reduced
    this.hubMusic = o.hubMusic; this.reduced = o.reduced
    if (changed) { this.applyStems(stemsFor(this.state, this.roundIndex, o), this.engine.ctx.currentTime); this.applyAmbience(ambienceFor(this.state, o.reduced)) }
  }

  /** Stop everything (hub exit, hard resync). */
  stopAll(): void { for (const h of this.active.values()) h.stop(AUDIO_STEM_XFADE_MS); this.active.clear(); for (const h of this.ambience.values()) h.stop(400); this.ambience.clear(); this.pending = null; this.stemStart = null }

  activeStems(): SoundId[] { return [...this.active.keys()] }
  activeAmbience(): SoundId[] { return [...this.ambience.keys()] }
  debug(): { state: MusicState; stems: SoundId[]; ambience: SoundId[]; pending: { at: number; stems: SoundId[] } | null; roundIndex: number } {
    return { state: this.state, stems: this.activeStems(), ambience: this.activeAmbience(), pending: this.pending, roundIndex: this.roundIndex }
  }
}

export { STEM_IDS, AMB_IDS }
